import { useMemo } from "react";
import { renderFormula } from "@/lib/chemicalFormula";
import type { Project } from "@/types/chemistry";
import { Badge } from "@/components/ui/badge";
import { Calendar, FlaskConical, Layers, Atom } from "lucide-react";
import { formatDate } from "@/lib/formatDate";

interface CompoundInfoTabProps {
  project: Project;
}

const statusLabel: Record<string, string> = {
  planning: "Planning",
  "in-progress": "In Progress",
  complete: "Complete"
};

const statusVariant: Record<string, "default" | "secondary" | "outline"> = {
  planning: "outline",
  "in-progress": "default",
  complete: "secondary"
};

export const CompoundInfoTab = ({ project }: CompoundInfoTabProps) => {
  // Build compound-level summaries: each root step = one compound
  const compoundSummaries = useMemo(() => {
    const rootSteps = project.steps.filter((s) => !s.parentId);

    // Collect all descendant steps for a given step id
    const collectDescendants = (id: string): typeof project.steps => {
      const children = project.steps.filter((s) => s.parentId === id);
      const all = [...children];
      for (const child of children) all.push(...collectDescendants(child.id));
      return all;
    };

    const seen = new Set<string>();
    const list: {name: string; formula: string; stepCount: number; variantCount: number;}[] = [];

    for (const root of rootSteps) {
      for (const c of root.products) {
        const key = c.casNumber || c.name || c.formula;
        if (key && !seen.has(key)) {
          seen.add(key);
          const descendants = collectDescendants(root.id);
          const allSteps = [root, ...descendants];
          const steps = allSteps.length;
          const variants = allSteps.reduce((acc, s) => acc + (s.variants?.length ?? 0), 0);
          list.push({ name: c.name || c.formula || "—", formula: c.formula, stepCount: steps, variantCount: variants });
        }
      }
    }
    return list;
  }, [project.steps]);

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Name */}
      <div>
        <p className="text-xs text-muted-foreground mb-1">Name</p>
        <p className="text-sm font-semibold text-foreground">
          {project.name || "—"}
        </p>
      </div>

      {/* Status */}
      




      

      {/* Description */}
      {project.description &&
      <div>
          <p className="text-xs text-muted-foreground mb-1">Description</p>
          <p className="text-sm text-foreground/80 leading-relaxed">
            {project.description}
          </p>
        </div>
      }

      {/* Compounds with per-compound summary */}
      {compoundSummaries.length > 0 &&
      <div>
          <p className="text-xs text-muted-foreground mb-1.5">Compounds ({compoundSummaries.length})</p>
          <div className="flex flex-col gap-2">
            {compoundSummaries.map((c, i) =>
          <div key={i} className="rounded bg-muted/40 px-2.5 py-2">
                <p className="text-xs font-medium text-foreground truncate" title={c.formula}>
                  {c.name}{c.name !== c.formula && c.formula && <span className="text-muted-foreground font-normal"> ({renderFormula(c.formula)})</span>}
                </p>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1"><FlaskConical className="h-3 w-3" />{c.stepCount} step{c.stepCount !== 1 ? "s" : ""}</span>
                  
                </div>
              </div>
          )}
          </div>
        </div>
      }

      {/* Deadline */}
      {project.deadline &&
      <div>
          <p className="text-xs text-muted-foreground mb-1">Deadline</p>
          <div className="flex items-center gap-1.5 text-sm text-foreground/80">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDate(project.deadline)}</span>
          </div>
        </div>
      }

      {/* Last modified */}
      <div>
        <p className="text-xs text-muted-foreground mb-1">Last Modified</p>
        <p className="text-sm text-foreground/60">
          {formatDate(project.lastModified)}
        </p>
      </div>
    </div>);

};